/**
 * This file is a generic fetch client that does not know about React or store life cycles.
 * It should be treated as simply a convenience wrapper around the generated API client.
 */
import { Dayjs } from 'dayjs'

import { getOnlyDate } from '~/astra'
import env from '~/env'

import { createBaseClient } from './base-client'
import { components, operations, paths } from './optimus-api-types.generated'
import { getPlanningDomainHeaders } from './planningDomainHeaders'
import { apiRefreshingMiddleware } from './unauthorizedHandlingMiddleware'

export const optimusBaseClient = createBaseClient<paths>({ baseUrl: `${env.VITE_API_BASE_URL}/optimus`, getRequestHeaders: getPlanningDomainHeaders })
optimusBaseClient.use(apiRefreshingMiddleware)

export type OptimusSchemas = components['schemas']

export type GanttChartSingleRoom = OptimusSchemas['GanttChartSingleRoom']
export type GanttEntry = OptimusSchemas['GanttEntry']
export type RetroMetrics = OptimusSchemas['RetroMetricsResponse']
export type PlanRetro = OptimusSchemas['PlanRetroResponse']
export type GanttData = components['schemas']['PlanRetroORDay'][]
export type PlanRetroGanttEntry = components['schemas']['PlanRetroGanttEntry']
export type PlanMetricDurationValue = OptimusSchemas['PlanMetricDurationValue']
export type PlanMetricCountValue = OptimusSchemas['PlanMetricCountValue']
export type AvailableTimes = OptimusSchemas['AvailableTimes']

export type CountRuleEvaluation = OptimusSchemas['CountRuleEvaluation']
export type DurationRuleEvaluation = OptimusSchemas['DurationRuleEvaluation']
export type RuleEvaluation = CountRuleEvaluation | DurationRuleEvaluation

export type RecurringAssignmentPromptRequest = OptimusSchemas['RecurringAssignmentPromptCreateRequest']

export type RecurringAssignmentPromptResponse =
    | OptimusSchemas['RecurringAssignmentSuccessfulCreateResponse']
    | OptimusSchemas['RecurringAssignmentErrorCreateResponse']

export type AvailableTimesRequest = {
    body: OptimusSchemas['EvaluateAvailableTimesRequest']
}

export type SurgeryDurationsRequest = {
    body: OptimusSchemas['SurgeryDurationsRequest']
}
export type GanttByRoomRequest = operations['get_gantt_data_single_room_retro_historic_data_gantt_single_room__room_code__get']['parameters']['query'] &
    operations['get_gantt_data_single_room_retro_historic_data_gantt_single_room__room_code__get']['parameters']['path']
export type RetroMetricsRequest = operations['get_metrics_retro_historic_data_metrics_get']['parameters']['query']
export type PredictedWaitingListRequest = {
    department_id: number
    start_date: string
    end_date: string
}

export type HospitalConstraintsRequest = {
    date: operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['parameters']['path']['date']
    body: OptimusSchemas['ConstraintCheckRequest']
}

export type HospitalConstraintsCandidateGroupRequest = {
    date: operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['parameters']['path']['date']
    body: OptimusSchemas['CandidateGroupCheckRequest']
}

export type HospitalConstraintsAvailableTimesRequest = {
    body: OptimusSchemas['CheckAvailableTimesRequest']
}

export type PostOpBookingStatusRequest = {
    params: { query: operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['parameters']['query'] }
    body: OptimusSchemas['ScheduledConstraintCheckRequest']
}

export type PostOpTacticalPlanStatusRequest = {
    params: { query: operations['postop_status_tactical_plan_hospital_constraints_postop_tactical_plan_status__get']['parameters']['query'] }
}

export type RetroPageRequest = operations['get_retro_page_retro_historic_data_retro_page_get']['parameters']['query']

export type PlanRetroParams = operations['get_plan_retro_retro_historic_data_plan_retro__block_schedule_id__get']['parameters']

export type BlockScheduleMetricsResponse = OptimusSchemas['BlockScheduleMetricsResponse']

export type RetroMetricsResponse = OptimusSchemas['RetroMetricsResponse']

export type ModelRunParameters = operations['put_run_staffing_plan_run__department_id___planning_period_id__put']['parameters']['query']
export type ModelGatherParameters = operations['get_gather_staffing_plan_gather__department_id___planning_period_id__get']['parameters']['query']

export type StaffingPlanRunValidationDetail = OptimusSchemas['StaffingPlanRunValidationDetail']

type FetchOptions = { signal?: AbortSignal }

/**
 * The client is a wrapper around .GET, .POST and so on methods that help bind the path and query parameters to the response objects.
 * This allows us to use to instantiate generic "import*" methods in the store.
 */
const optimusClient = {
    getBlockScheduleMetrics: async (block_schedule_ids: number[], use_batch_api_data: boolean, fetchOptions?: FetchOptions) =>
        await optimusBaseClient.GET('/plan-metrics/block_schedule_metrics', {
            params: {
                query: {
                    ids: block_schedule_ids,
                    use_batch_api_data,
                },
            },
            signal: fetchOptions?.signal,
        }),
    getGanttDataByRoomCode: async ({ start_date, end_date, room_code, use_batch_api_data }: GanttByRoomRequest, fetchOptions?: FetchOptions) =>
        await optimusBaseClient.GET('/retro/historic_data/gantt_single_room/{room_code}', {
            params: { query: { start_date, end_date, use_batch_api_data }, path: { room_code } },
            signal: fetchOptions?.signal,
        }),
    getPlanningPeriodMetrics: async (planning_period_id: number, fetchOptions?: FetchOptions) =>
        await optimusBaseClient.GET('/plan-metrics/planning_periods/{planning_period_id}', {
            params: { path: { planning_period_id } },
            signal: fetchOptions?.signal,
        }),
    getRetroMetrics: async ({ start_date, end_date, room_code, use_batch_api_data }: RetroMetricsRequest, fetchOptions?: FetchOptions) =>
        await optimusBaseClient.GET('/retro/historic_data/metrics', {
            params: {
                query: {
                    start_date,
                    end_date,
                    room_code,
                    use_batch_api_data,
                },
            },
            signal: fetchOptions?.signal,
        }),
    getPlanRetro: async (params: PlanRetroParams, fetchOptions?: FetchOptions) =>
        await optimusBaseClient.GET('/retro/historic_data/plan_retro/{block_schedule_id}', {
            params,
            signal: fetchOptions?.signal,
        }),
    getRetroPageData: async (
        { main_end_date, main_start_date, selected_room_code, time_series_range_end_dates, use_batch_api_data }: RetroPageRequest,
        fetchOptions?: FetchOptions
    ) =>
        await optimusBaseClient.GET('/retro/historic_data/retro_page', {
            params: {
                query: {
                    main_start_date,
                    main_end_date,
                    selected_room_code,
                    time_series_range_end_dates,
                    use_batch_api_data,
                },
            },
            signal: fetchOptions?.signal,
        }),
    getOvertimeMetrics: async (block_schedule_ids: number[], fetchOptions?: FetchOptions) =>
        await optimusBaseClient.GET('/plan-metrics/block_schedule_overtime', {
            params: { query: { ids: block_schedule_ids } },
            signal: fetchOptions?.signal,
        }),
    availableTimes: {
        getEvaluations: async ({ body }: AvailableTimesRequest, fetchOptions?: FetchOptions) =>
            await optimusBaseClient.POST('/available-times/', {
                body,
                signal: fetchOptions?.signal,
            }),
    },
    duration: {
        getSurgeryDurations: async ({ body }: SurgeryDurationsRequest, fetchOptions?: FetchOptions) =>
            await optimusBaseClient.POST('/duration/surgeries', {
                body,
                signal: fetchOptions?.signal,
            }),
    },
    staffingPlan: {
        gather: async (department_id: number, planning_period_id: number, queryParams?: ModelGatherParameters) =>
            await optimusBaseClient.GET('/staffing-plan/gather/{department_id}/{planning_period_id}', {
                params: {
                    path: { department_id, planning_period_id },
                    query: queryParams,
                },
            }),
        postRun: async (department_id: number, planning_period_id: number, queryParams?: ModelRunParameters) =>
            await optimusBaseClient.PUT('/staffing-plan/run/{department_id}/{planning_period_id}', {
                params: {
                    path: {
                        department_id,
                        planning_period_id,
                    },
                    query: queryParams,
                },
            }),
        rejectRun: async (staffing_plan_execution_id: number, planning_period_id: number) =>
            await optimusBaseClient.POST('/staffing-plan/reject/{staffing_plan_execution_id}/{planning_period_id}', {
                params: { path: { staffing_plan_execution_id, planning_period_id } },
            }),
        approveRun: async (staffing_plan_execution_id: number) =>
            await optimusBaseClient.PUT('/staffing-plan/approve/{staffing_plan_execution_id}', {
                params: { path: { staffing_plan_execution_id } },
            }),
        discardFailedRun: async (staffing_plan_execution_id: number) =>
            await optimusBaseClient.PUT('/staffing-plan/discard/{staffing_plan_execution_id}', {
                params: { path: { staffing_plan_execution_id } },
            }),
        stopRun: async (staffing_plan_execution_id: number) =>
            await optimusBaseClient.PUT('/staffing-plan/stop/{staffing_plan_execution_id}', {
                params: { path: { staffing_plan_execution_id } },
            }),
        getAlerts: async (department_id: number, planning_period_id: number, fetchOptions?: FetchOptions) =>
            await optimusBaseClient.GET('/staffing-plan/alerts/{department_id}/{planning_period_id}', {
                params: {
                    path: {
                        department_id,
                        planning_period_id,
                    },
                },
                signal: fetchOptions?.signal,
            }),
        getEvaluation: async (department_id: number, planning_period_id: number, fetchOptions?: FetchOptions) =>
            await optimusBaseClient.GET('/staffing-plan/assignment_requests/evaluate/{department_id}/{planning_period_id}', {
                params: {
                    path: {
                        department_id,
                        planning_period_id,
                    },
                },
                signal: fetchOptions?.signal,
            }),
        getSurgeonWaitingListPrediction: async (body: OptimusSchemas['SurgeonWaitingListsRequest'], fetchOptions?: FetchOptions) =>
            await optimusBaseClient.POST('/waiting-list/', {
                body,
                signal: fetchOptions?.signal,
            }),
        invalidateInputConfigCache: async () => await optimusBaseClient.POST('/staffing-plan/config/invalidate_cache'),
    },
    recurringAssignments: {
        createPrompt: async (data: RecurringAssignmentPromptRequest) =>
            await optimusBaseClient.POST('/staffing-plan/recurring_assignments/prompt', { body: data }),
        apply: async (data: components['schemas']['RecurringAssignmentApplyRequest']) =>
            await optimusBaseClient.POST('/staffing-plan/recurring_assignments/apply', { body: data }),
    },
    surgeryProgram: {
        // POST Operations
        suggests: async (data: OptimusSchemas['SurgeryProgramRequest']) => await optimusBaseClient.POST('/surgery_program_jobs/', { body: data }),

        cancel: async (planning_period_id: number) =>
            await optimusBaseClient.POST('/surgery_program_jobs/latest/{planning_period_id}/cancel', {
                params: { path: { planning_period_id } },
            }),

        testEnvOnlyClearAstraGeneratedPlan: async (planning_period_id: number) =>
            await optimusBaseClient.POST('/surgery_program_jobs/testing/clear_capacity/{planning_period_id}', {
                params: { path: { planning_period_id } },
            }),

        // GET Operations
        getStatus: async (planning_period_id: number) =>
            await optimusBaseClient.GET('/surgery_program_jobs/latest/{planning_period_id}', { params: { path: { planning_period_id } } }),
    },
    fetchSurgeryProgramJob: async (id: number) => await optimusBaseClient.GET('/surgery_program_jobs/{id}', { params: { path: { id } } }),
    fetchSurgeryProgramJobs: async (query: operations['getSurgeryProgramJobs']['parameters']['query']) =>
        await optimusBaseClient.GET('/surgery_program_jobs/', { params: { query } }),
    getWardOccupancy: async (startDate: string, endDate: string, fetchOptions?: FetchOptions) =>
        await optimusBaseClient.GET('/plan-metrics/ward_occupancy', {
            params: { query: { start_date: startDate, end_date: endDate } },
            signal: fetchOptions?.signal,
        }),
    insights: {
        getWaitingListDemandPredictions: async (
            surgeries: OptimusSchemas['ScheduledDipsSurgery'][],
            prediction_end_date: Dayjs,
            use_surgery_duration_model: boolean,
            shadow_run_surgery_duration_model: boolean
        ) =>
            await optimusBaseClient.POST('/insights/specialty_demand_predictions/', {
                body: {
                    surgeries,
                    prediction_end_date: getOnlyDate(prediction_end_date),
                    use_surgery_duration_model,
                    shadow_run_surgery_duration_model,
                },
            }),
        getHistoricWaitingTimes: async () => await optimusBaseClient.POST('/insights/historic_waiting_times/'),
        getHistoricFlows: async () => await optimusBaseClient.POST('/insights/historic_flows/'),
    },
} as const

export default optimusClient
